import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSetupReturn } from "@/lib/setupFlow";
import api from "@/lib/api";
import { fmtTRY, fmtDate, PAYMENT_STATUS_LABEL } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState, StatusBadge, StatCard, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, Wallet, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PaymentsDialog from "@/components/PaymentsDialog";
import PurchaseFormDialog from "@/components/PurchaseFormDialog";
import DateRangeFilter from "@/components/DateRangeFilter";

const PAGE_LIMIT = 25;

export default function Purchases() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [aggregate, setAggregate] = useState({ total_amount: 0, total_paid: 0, outstanding: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paymentsFor, setPaymentsFor] = useState(null);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = {
        page: opts.page ?? page,
        limit: PAGE_LIMIT,
        ...(search ? { search } : {}),
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(customerFilter !== "all"
          ? { customer_id: customerFilter === "none" ? "none" : customerFilter }
          : {}),
        // Tarih filtresi backend'e date_from / date_to olarak gider;
        // ``purchases.date`` (satın alma tarihi) üzerinde inclusive
        // eşleşme yapar.  Boş ise gönderilmez.
        ...(dateRange.from ? { date_from: dateRange.from } : {}),
        ...(dateRange.to   ? { date_to:   dateRange.to   } : {}),
      };
      const { data } = await api.get("/purchases", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setAggregate(data.aggregate || { total_amount: 0, total_paid: 0, outstanding: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Satın alma listesi yüklenemedi");
    } finally { setLoading(false); }
  }, [page, search, statusFilter, customerFilter, dateRange]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Dashboard FAB'dan ``?new=1`` ile geldiyse Yeni Satın Alma dialog'unu aç.
  useSetupReturn("purchase", open);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      setEditing(null); setOpen(true);
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    Promise.all([
      api.get("/lookups/vendors"),
      api.get("/lookups/customers"),
      api.get("/jobs"),
      api.get("/categories"),
    ]).then(([v, c, j, cat]) => {
      setVendors(v.data || []);
      setCustomers(c.data || []);
      setJobs(j.data || []);
      setCategories(cat.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_LIMIT));

  const onEdit = async (row) => {
    try {
      const { data } = await api.get(`/purchases/${row.id}`);
      setEditing(data);
      setOpen(true);
    } catch { toast.error("Satın alma yüklenemedi"); }
  };
  const openPayments = async (row) => {
    try {
      const { data } = await api.get(`/purchases/${row.id}`);
      setPaymentsFor(data);
    } catch { toast.error("Ödemeler yüklenemedi"); }
  };
  const onDelete = async (row) => {
    if (!window.confirm("Bu satın alma silinsin mi?")) return;
    await api.delete(`/purchases/${row.id}`);
    toast.success("Silindi"); load();
  };

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <StickyPageHead>
      <PageHeader title="Satın Almalar" subtitle="Tedarikçilerden yapılan satın almalar — her biri bir müşteri için"
        action={
          <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg"
                  onClick={() => { setEditing(null); setOpen(true); }}
                  data-testid="add-purchase-button">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Satın Alma
          </Button>
        }
      />
      </StickyPageHead>

      <PurchaseFormDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
        editing={editing}
        vendors={vendors}
        customers={customers}
        jobs={jobs}
        categories={categories}
        onSaved={load}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Toplam Satın Alma" value={fmtTRY(aggregate.total_amount)} accent="slate" testId="purchases-total" />
        <StatCard label="Ödenen" value={fmtTRY(aggregate.total_paid)} accent="emerald" />
        <StatCard label="Bekleyen Borç" value={fmtTRY(aggregate.outstanding)} accent="amber" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Satın alma ara..." className="pl-9 h-10 rounded-lg" data-testid="purchases-search-input" />
        </div>
        <Select value={customerFilter} onValueChange={(v) => { setCustomerFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48 h-10 rounded-lg" data-testid="purchases-customer-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Müşteriler</SelectItem>
            <SelectItem value="none">Genel Şirket Giderleri</SelectItem>
            {(customers ?? []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 h-10 rounded-lg" data-testid="purchases-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Ödemeler</SelectItem>
            {Object.entries(PAYMENT_STATUS_LABEL ?? {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateRangeFilter
          value={dateRange}
          onChange={(v) => { setDateRange(v); setPage(1); }}
          testId="purchases-date-range"
        />
      </div>

      <SectionCard>
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="purchases-loading">Yükleniyor…</div>
        ) : items.length === 0 ? (
          <EmptyState title="Satın alma bulunamadı" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/50">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3 font-semibold">Tarih</th>
                    <th className="px-5 py-3 font-semibold">Tedarikçi</th>
                    <th className="px-5 py-3 font-semibold">Müşteri / İş</th>
                    <th className="px-5 py-3 font-semibold">Açıklama</th>
                    <th className="px-5 py-3 font-semibold text-right">Tutar</th>
                    <th className="px-5 py-3 font-semibold text-right">Ödenen</th>
                    <th className="px-5 py-3 font-semibold">Ödeme</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((it) => (
                    <tr key={it.id} className="hover:bg-slate-50/60" data-testid={`purchase-row-${it.id}`}>
                      <td className="px-5 py-3 whitespace-nowrap">{fmtDate(it.date)}</td>
                      <td className="px-5 py-3 font-medium">{it.vendor_name}</td>
                      <td className="px-5 py-3">
                        {it.is_general ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid={`purchase-general-badge-${it.id}`}>
                            Genel Şirket Gideri
                          </span>
                        ) : (
                          <>
                            <div className="text-slate-800">{it.customer_name}</div>
                            <div className="text-xs text-slate-500">{it.job_name || "—"}</div>
                          </>
                        )}
                      </td>
                      <td className="px-5 py-3 max-w-xs">
                        <button
                          type="button"
                          onClick={() => onEdit(it)}
                          className="text-left w-full text-slate-700 hover:text-slate-900 hover:underline truncate cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded"
                          data-testid={`purchase-title-${it.id}`}
                          title="Detayı düzenle"
                        >
                          {it.description || "—"}
                        </button>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">{fmtTRY(it.amount)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-emerald-600">{fmtTRY(it.paid_amount)}</td>
                      <td className="px-5 py-3"><StatusBadge value={it.payment_status} label={PAYMENT_STATUS_LABEL[it.payment_status]} /></td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => openPayments(it)} data-testid={`purchase-payments-${it.id}`}>
                            <Wallet className="w-3.5 h-3.5 mr-1" /> Ödemeler
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(it)} data-testid={`purchase-edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onDelete(it)} data-testid={`purchase-delete-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} onPage={setPage} />
          </>
        )}
      </SectionCard>

      <PaymentsDialog
        key={paymentsFor?.id}
        open={!!paymentsFor}
        onOpenChange={(v) => { if (!v) setPaymentsFor(null); }}
        type="purchase"
        record={paymentsFor}
        onUpdated={() => load()}
      />
    </div>
  );
}

function Pagination({ page, totalPages, total, onPage }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 text-xs text-slate-600" data-testid="purchases-pagination">
      <div>
        Toplam <strong className="text-slate-800">{total}</strong> kayıt · Sayfa <strong>{page}</strong> / {totalPages}
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="purchases-prev-page">
          <ChevronLeft className="w-3.5 h-3.5" /> Önceki
        </Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="purchases-next-page">
          Sonraki <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
